// src/components/Dashboard/RoleDashboard/RoleExtras/ViewApplications.jsx
import React, { useEffect, useMemo, useRef, useState } from "react";
import { apiFetch } from "../../../../apiFetch";
import "./ViewApplications.css";
import { useNavigate, useParams } from "react-router-dom";
import { FaStar, FaRegStar } from "react-icons/fa";

const VALID_ROLES = ["agent", "scout", "club"];
const DATE_PRESETS = ["today", "last7", "last30", "thisMonth", "custom"];

const isDev =
    typeof process !== "undefined"
        ? process.env.NODE_ENV !== "production"
        : true;

function getCurrentRoleAndId() {
    if (typeof window === "undefined") return { role: "agent", id: null };

    const rawRole = (
        (localStorage.getItem("role") || localStorage.getItem("userRole")) ||
        "agent"
    ).toLowerCase();

    const role = VALID_ROLES.includes(rawRole) ? rawRole : "agent";

    const idKey =
        role === "agent" ? "agentId" : role === "scout" ? "scoutId" : "clubId";

    const id = localStorage.getItem(idKey) || null;
    if (isDev) console.debug("[ViewApplications] role/id", { role, id, idKey });

    return { role, id };
}

function formatLink(url) {
    if (!url) return "#";
    return /^https?:\/\//i.test(url) ? url : `https://${url}`;
}

function pad2(value) {
    return String(value).padStart(2, "0");
}

function toLocalDateInputValue(date) {
    if (!(date instanceof Date) || Number.isNaN(date.getTime())) return "";
    return `${date.getFullYear()}-${pad2(date.getMonth() + 1)}-${pad2(date.getDate())}`;
}

function getPresetDateRange(preset) {
    const now = new Date();

    switch (preset) {
        case "today":
            return {
                from: toLocalDateInputValue(now),
                to: toLocalDateInputValue(now),
            };

        case "last7": {
            const from = new Date(now);
            from.setDate(now.getDate() - 6);
            return {
                from: toLocalDateInputValue(from),
                to: toLocalDateInputValue(now),
            };
        }

        case "last30": {
            const from = new Date(now);
            from.setDate(now.getDate() - 29);
            return {
                from: toLocalDateInputValue(from),
                to: toLocalDateInputValue(now),
            };
        }

        case "thisMonth": {
            const first = new Date(now.getFullYear(), now.getMonth(), 1);
            return {
                from: toLocalDateInputValue(first),
                to: toLocalDateInputValue(now),
            };
        }

        default:
            return { from: "", to: "" };
    }
}

function getDateFilterLabel(datePreset, fromDate, toDate) {
    switch (datePreset) {
        case "today":
            return "Today";
        case "last7":
            return "Last 7 days";
        case "last30":
            return "Last 30 days";
        case "thisMonth":
            return "This month";
        case "custom":
            if (fromDate && toDate) return `${fromDate} → ${toDate}`;
            if (fromDate) return `From ${fromDate}`;
            if (toDate) return `Until ${toDate}`;
            return "Custom range";
        default:
            return "All dates";
    }
}

const ViewApplications = () => {
    const [roleInfo, setRoleInfo] = useState({ role: "agent", id: null });
    const [applications, setApplications] = useState([]);
    const [starredIds, setStarredIds] = useState([]);
    const [loading, setLoading] = useState(true);
    const [loadError, setLoadError] = useState("");

    // backend pagination state
    const [page, setPage] = useState(1);
    const [pageSize] = useState(25);
    const [total, setTotal] = useState(0);
    const [totalPages, setTotalPages] = useState(1);

    // date filter state
    const defaultRange = getPresetDateRange("last30");
    const [datePreset, setDatePreset] = useState("last30");
    const [fromDate, setFromDate] = useState(defaultRange.from);
    const [toDate, setToDate] = useState(defaultRange.to);

    const navigate = useNavigate();
    const rootRef = useRef(null);

    // id from /opportunity/applications/:id (opportunity filter)
    const { id: opportunityFilterIdParam } = useParams();
    const opportunityFilterId = opportunityFilterIdParam || null;

    // 🔻 Hide global header/footer while this page is shown
    useEffect(() => {
        const selectors = [
            "header",
            ".header",
            ".site-header",
            ".topbar",
            ".app-header",
            "nav[role='navigation']",
            "footer",
            ".footer",
            ".site-footer",
            ".app-footer",
        ];

        const backups = [];
        selectors.forEach((sel) => {
            try {
                document.querySelectorAll(sel).forEach((el) => {
                    if (rootRef.current && rootRef.current.contains(el)) return;
                    backups.push({ el, prevDisplay: el.style.display || "" });
                    el.style.display = "none";
                });
            } catch (e) {
                // ignore selector errors
            }
        });

        return () => {
            backups.forEach(({ el, prevDisplay }) => {
                try {
                    el.style.display = prevDisplay || "";
                } catch (e) {
                    // ignore restore errors
                }
            });
        };
    }, []);

    // Determine role + id on mount
    useEffect(() => {
        const info = getCurrentRoleAndId();
        setRoleInfo(info);
    }, []);

    // Reset to first page when filters change
    useEffect(() => {
        setPage(1);
    }, [opportunityFilterId, datePreset, fromDate, toDate]);

    // Fetch data when role/id known or filters/page change
    useEffect(() => {
        const { role, id } = roleInfo;

        if (!id) {
            if (isDev) {
                console.error(
                    "❌ No role-specific ID found in localStorage (agentId / scoutId / clubId)"
                );
            }
            setLoading(false);
            setLoadError("Missing role ID in local storage.");
            return;
        }

        const fetchData = async () => {
            setLoading(true);
            setLoadError("");

            try {
                const params = new URLSearchParams();
                params.set("page", String(page));
                params.set("pageSize", String(pageSize));

                if (opportunityFilterId) {
                    params.set("opportunityId", String(opportunityFilterId));
                }

                if (datePreset === "custom") {
                    if (fromDate) params.set("from", fromDate);
                    if (toDate) params.set("to", toDate);
                } else if (DATE_PRESETS.includes(datePreset) && datePreset !== "custom") {
                    params.set("range", datePreset);
                }

                const appsPath = `/api/applications-by/${role}/${id}?${params.toString()}`;
                const starsPath = `/api/starred-applications/${role}/${id}`;

                if (isDev) {
                    console.info("[ViewApplications] fetching", {
                        appsPath,
                        starsPath,
                        opportunityFilterId,
                        page,
                        pageSize,
                        datePreset,
                        fromDate,
                        toDate,
                    });
                }

                const [appsRes, starsRes] = await Promise.all([
                    apiFetch(appsPath),
                    apiFetch(starsPath),
                ]);

                if (!appsRes.ok || !starsRes.ok) {
                    throw new Error(
                        `Fetch failed: apps=${appsRes.status}, stars=${starsRes.status}`
                    );
                }

                const appsData = await appsRes.json();
                const starsData = await starsRes.json();

                // Backward compatible with old array response AND new paginated object response
                if (Array.isArray(appsData)) {
                    setApplications(appsData);
                    setTotal(appsData.length);
                    setTotalPages(1);
                } else {
                    setApplications(Array.isArray(appsData.items) ? appsData.items : []);
                    setTotal(Number(appsData.total) || 0);
                    setTotalPages(Math.max(Number(appsData.totalPages) || 1, 1));
                }

                setStarredIds(
                    Array.isArray(starsData) ? starsData.map((s) => s.applicationId) : []
                );
            } catch (error) {
                console.error("[ViewApplications] Error fetching data:", error);
                setLoadError(error.message || "Failed to load applications.");
            } finally {
                setLoading(false);
            }
        };

        fetchData();
    }, [
        roleInfo.role,
        roleInfo.id,
        opportunityFilterId,
        page,
        pageSize,
        datePreset,
        fromDate,
        toDate,
    ]);

    const { role, id: roleId } = roleInfo;

    const toggleStar = async (applicationId) => {
        if (!roleId) return;

        try {
            const isStarred = starredIds.includes(applicationId);
            const payload = { role, roleId, applicationId };

            if (isStarred) {
                await apiFetch("/api/unstar-application", {
                    method: "DELETE",
                    headers: { "Content-Type": "application/json" },
                    body: JSON.stringify(payload),
                });

                setStarredIds((prev) => prev.filter((id) => id !== applicationId));
            } else {
                await apiFetch("/api/star-application", {
                    method: "POST",
                    headers: { "Content-Type": "application/json" },
                    body: JSON.stringify(payload),
                });

                setStarredIds((prev) => [...prev, applicationId]);
            }
        } catch (err) {
            console.error("[ViewApplications] Error toggling star:", err);
        }
    };

    const handlePresetChange = (preset) => {
        if (!DATE_PRESETS.includes(preset)) return;

        setDatePreset(preset);

        if (preset !== "custom") {
            const range = getPresetDateRange(preset);
            setFromDate(range.from);
            setToDate(range.to);
        }
    };

    const clearDateFilter = () => {
        setDatePreset("custom");
        setFromDate("");
        setToDate("");
    };

    // Keep this as a safety fallback for the old non-query backend path.
    // With the new backend query params, opportunity filtering should already happen server-side.
    const visibleApplications = useMemo(() => {
        return opportunityFilterId
            ? applications.filter((app) => {
                const oppId =
                    app.opportunityId ||
                    (app.opportunity && (app.opportunity.id || app.opportunity._id));
                return String(oppId) === String(opportunityFilterId);
            })
            : applications;
    }, [applications, opportunityFilterId]);

    const titleByRole = {
        agent: "Player Applications",
        scout: "Player Applications (Scout View)",
        club: "Player Applications (Club View)",
    };

    const pageTitle =
        opportunityFilterId && titleByRole[role]
            ? `${titleByRole[role]} – Opportunity #${opportunityFilterId}`
            : titleByRole[role] || "Player Applications";

    const totalApplications =
        total > 0 || page > 1 ? total : visibleApplications.length;

    const totalStarred = visibleApplications.filter((app) =>
        starredIds.includes(app.id)
    ).length;

    const activeDateLabel = getDateFilterLabel(datePreset, fromDate, toDate);
    const isLegacyArrayMode = totalPages === 1 && total === visibleApplications.length;

    return (
        <div
            ref={rootRef}
            className={`role-view-applications ${role}-view-applications`}
        >
            <div className="applications-container">
                <h2 className="applications-title">{pageTitle}</h2>

                {/* summary bar */}
                <div className="applications-summary">
                    <span>{totalApplications} applications</span>
                    <span>{totalStarred} starred</span>
                    {opportunityFilterId && (
                        <span className="summary-chip">
                            Opportunity #{opportunityFilterId}
                        </span>
                    )}
                    <span className="summary-chip">Date: {activeDateLabel}</span>
                </div>

                {/* compact filter row */}
                <div className="applications-filters compact-filters">
                    <label className="date-filter-label">
                        <span>Filter</span>
                        <select
                            className="date-filter-select"
                            value={datePreset}
                            onChange={(e) => handlePresetChange(e.target.value)}
                        >
                            <option value="today">Today</option>
                            <option value="last7">Last 7 days</option>
                            <option value="last30">Last 30 days</option>
                            <option value="thisMonth">This month</option>
                            <option value="custom">Custom range</option>
                        </select>
                    </label>

                    <label className="date-filter-label">
                        <span>From</span>
                        <input
                            type="date"
                            className="date-filter-input"
                            value={fromDate}
                            onChange={(e) => {
                                setDatePreset("custom");
                                setFromDate(e.target.value);
                            }}
                        />
                    </label>

                    <label className="date-filter-label">
                        <span>To</span>
                        <input
                            type="date"
                            className="date-filter-input"
                            value={toDate}
                            onChange={(e) => {
                                setDatePreset("custom");
                                setToDate(e.target.value);
                            }}
                        />
                    </label>

                    <button
                        type="button"
                        className="filter-clear-btn"
                        onClick={clearDateFilter}
                    >
                        Clear
                    </button>
                </div>

                {loading ? (
                    <p className="loading-text">Loading...</p>
                ) : loadError ? (
                    <p className="no-applications-text" style={{ color: "red" }}>
                        {loadError}
                    </p>
                ) : visibleApplications.length === 0 ? (
                    <p className="no-applications-text">
                        {opportunityFilterId
                            ? "No applications for this opportunity yet."
                            : "No applications submitted yet."}
                    </p>
                ) : (
                    <>
                        {visibleApplications.map((app, index) => (
                            <div className="application-card" key={app.id}>
                                <div className="application-header">
                                    <div>
                                        <div className="application-number">
                                            Application #{(page - 1) * pageSize + index + 1}
                                        </div>

                                        {app.opportunity && (
                                            <span
                                                className={`status-pill status-${(
                                                    app.opportunity.status || "Open"
                                                ).toLowerCase()}`}
                                            >
                                                {app.opportunity.status || "Open"}
                                            </span>
                                        )}

                                        {starredIds.includes(app.id) && (
                                            <span className="starred-chip">★ Starred</span>
                                        )}
                                    </div>

                                    <button
                                        className="star-button"
                                        onClick={() => toggleStar(app.id)}
                                        title={
                                            starredIds.includes(app.id)
                                                ? "Unstar"
                                                : "Star this application"
                                        }
                                    >
                                        {starredIds.includes(app.id) ? (
                                            <FaStar color="gold" />
                                        ) : (
                                            <FaRegStar />
                                        )}
                                    </button>
                                </div>

                                {app.opportunity && (
                                    <>
                                        <p className="application-field">
                                            <strong>Posted Opportunity:</strong>{" "}
                                            {app.opportunity.description}
                                        </p>

                                        {app.opportunity.link && (
                                            <p className="application-field">
                                                <strong>Link:</strong>{" "}
                                                <a
                                                    href={formatLink(app.opportunity.link)}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                >
                                                    {app.opportunity.link}
                                                </a>
                                            </p>
                                        )}
                                    </>
                                )}

                                <p className="application-field">
                                    <strong>Application:</strong> {app.introduction}
                                </p>

                                <p className="application-field">
                                    <strong>Player Profile:</strong>{" "}
                                    <button
                                        className="view-profile-btn"
                                        onClick={() => {
                                            const playerSlug =
                                                app.player?.profileSlug ||
                                                app.profileSlug ||
                                                app.playerProfileSlug ||
                                                null;

                                            if (!playerSlug) {
                                                alert("Player profile slug is missing.");
                                                console.error(
                                                    "Missing playerSlug in application:",
                                                    app
                                                );
                                                return;
                                            }

                                            navigate(`/player-profile/${playerSlug}`, {
                                                state: {
                                                    viewer: role,
                                                    playerId: app.playerId,
                                                    playerSlug,
                                                },
                                            });
                                        }}
                                    >
                                        View Profile
                                    </button>
                                </p>

                                {app.cvUrl && (
                                    <p className="application-field">
                                        <strong>CV:</strong>{" "}
                                        <a
                                            href={formatLink(app.cvUrl)}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                        >
                                            Download CV
                                        </a>
                                    </p>
                                )}

                                {app.appliedAt && (
                                    <p className="application-date">
                                        Applied at: {new Date(app.appliedAt).toLocaleString()}
                                    </p>
                                )}
                            </div>
                        ))}

                        {!isLegacyArrayMode && totalPages > 1 && (
                            <div className="applications-pagination">
                                <button
                                    type="button"
                                    className="filter-chip"
                                    disabled={page <= 1}
                                    onClick={() => setPage((prev) => Math.max(prev - 1, 1))}
                                >
                                    Previous
                                </button>

                                <span className="pagination-text">
                                    Page {page} of {totalPages}
                                </span>

                                <button
                                    type="button"
                                    className="filter-chip"
                                    disabled={page >= totalPages}
                                    onClick={() =>
                                        setPage((prev) => Math.min(prev + 1, totalPages))
                                    }
                                >
                                    Next
                                </button>
                            </div>
                        )}
                    </>
                )}
            </div>
        </div>
    );
};

export default ViewApplications;




